<template lang="pug">
  EditDialog(
    ref="editDialog"
    title="設定"
    :hasTitleSuffix="false"
    :displayFooter="false"
    :displayUpdateInfo="false"
    width="800px"
    @closeEditDialog="close"
  )
    template(slot="body")
      v-container
        v-layout(column)
          v-flex
            v-subheader.px-0 通知設定
            v-card.mb-3
              v-card-text
                v-layout(row align-center)
                  v-flex(xs8)
                    div PUSH通知の許可
                    div.caption ブラウザでPUSH通知を受信するための許可を取得します
                    div(class="mt-2")
                      v-chip(
                        v-if="currentPermissionStatus"
                        :color="getPermissionChipColor()"
                        text-color="white"
                        small
                        class="mr-2"
                      ) {{ getPermissionStatusText() }}
                      v-chip(
                        v-if="isDeviceRegistered"
                        color="success"
                        text-color="white"
                        small
                      ) 登録済み
                  v-flex(xs4 text-xs-right)
                    v-btn(
                      :color="isDeviceRegistered ? 'success' : 'primary'"
                      :loading="registering"
                      @click="registerForNotifications"
                      :disabled="registering"
                    ) {{ isDeviceRegistered ? '更新' : '登録' }}
            v-card(v-if="showGuide" color="warning" class="mt-3")
              v-card-text
                v-layout(column)
                  div.subheading.mb-2 通知許可の手動設定が必要です
                  div.body-2.mb-3 {{ getPermissionMessage() }}
                  div.body-2.mb-2 ブラウザ別設定方法：
                  div.caption.mb-1 <strong>Chrome:</strong> アドレスバーの🔒アイコン → 通知 → 許可
                  div.caption.mb-1 <strong>Firefox:</strong> アドレスバーの🛡️アイコン → 通知許可
                  div.caption.mb-1 <strong>Edge:</strong> アドレスバーの🔒アイコン → 通知 → 許可
                  div.caption.mb-3 <strong>Safari:</strong> Safari → 環境設定 → Webサイト → 通知
                  v-btn(
                    color="primary"
                    @click="retryPermission"
                    :loading="registering"
                  ) 再試行

          v-flex.mt-4
            v-subheader.px-0 登録済みデバイス情報
            v-progress-linear(v-if="loadingDeviceList" indeterminate color="primary")
            v-layout(column v-else-if="deviceList.length > 0")
              v-card.mb-2(v-for="device in deviceList" :key="device.id" outlined)
                v-card-text
                  v-layout(row wrap align-center)
                    v-flex(xs11)
                      v-layout(row wrap)
                        v-flex(xs12 sm6)
                          div.caption.grey--text デバイス名
                          div.body-2 {{ device.deviceInfo?.deviceName || '不明' }}
                        v-flex(xs12 sm6)
                          div.caption.grey--text OS
                          div.body-2 {{ getOsInfo(device) }}
                        v-flex(xs12 sm6)
                          div.caption.grey--text ブラウザ
                          div.body-2 {{ getBrowserInfo(device) }}
                        v-flex(xs12 sm6)
                          div.caption.grey--text 最終更新
                          div.body-2 {{ formatDateTime(device.updatedAt) }}
                    v-flex(xs1 text-xs-right)
                      v-btn(
                        icon
                        color="error"
                        @click="confirmDeleteDevice(device)"
                        :disabled="deletingDeviceId === device.id"
                      )
                        v-icon close
            div(v-else)
              v-alert(:value="true" type="info" outline) 登録済みデバイスはありません

            DeleteConfirmDialog(
              ref="deleteConfirmDialog"
              title="デバイス削除確認"
              :message="deleteConfirmMessage"
              @yes="executeDeleteDevice"
            )
</template>

<script>
import { messaging } from "@/composables/firebase/firebase";
import { getToken, isSupported } from "firebase/messaging";
import { mapActions, mapGetters } from "vuex";
import EditDialog from "@/components/common/dialog/EditDialog";
import DeleteConfirmDialog from "@/components/common/dialog/DeleteConfirmDialog";

export default {
  name: "SettingsDialog",
  components: {
    EditDialog,
    DeleteConfirmDialog
  },
  data() {
    return {
      registering: false,
      showGuide: false,
      permissionStatus: null,
      currentPermissionStatus: null,
      isDeviceRegistered: false,
      currentToken: null,
      loadingDeviceList: false,
      deviceToDelete: null,
      deletingDeviceId: null
    };
  },
  computed: {
    ...mapGetters("notification", ["deviceList"]),
    deleteConfirmMessage() {
      if (!this.deviceToDelete) return "";
      const deviceName = this.deviceToDelete.deviceInfo?.deviceName || "不明なデバイス";
      const os = this.getOsInfo(this.deviceToDelete);
      return `<p><strong>${deviceName}</strong> (${os}) を削除してもよろしいですか？</p><p>このデバイスへのPUSH通知が送信されなくなります。</p>`;
    }
  },
  methods: {
    ...mapActions("notification", {
      postSaveDevice: "postSaveDevice",
      fetchDeviceList: "fetchDeviceList",
      deleteDevice: "deleteDevice"
    }),
    async open() {
      this.$refs.editDialog.openDialog();
      this.checkCurrentPermissionStatus();
      await this.checkDeviceRegistration();
      await this.loadDeviceList();
    },
    close() {
      // EditDialog側でcloseEditDialogイベントが発火されるため、特に処理不要
    },
    async loadDeviceList() {
      this.loadingDeviceList = true;
      try {
        await this.fetchDeviceList();
      } catch (error) {
        console.error("Error loading device list:", error);
      } finally {
        this.loadingDeviceList = false;
      }
    },
    async registerForNotifications() {
      this.registering = true;
      try {
        await this.requestNotificationPermission();
        // 登録成功後、デバイスリストを再読み込み
        await this.loadDeviceList();
      } catch (error) {
        console.error("Error registering for notifications:", error);
        alert("通知の登録に失敗しました。");
      } finally {
        this.registering = false;
      }
    },
    async requestNotificationPermission() {
      if (!messaging) {
        alert("このブラウザではPUSH通知がサポートされていません。");
        return;
      }

      // 1. Firebase Messaging がサポートされているか確認
      let supported = false;
      try {
        console.log("Checking isSupported...");
        supported = await Promise.race([
          isSupported(),
          new Promise((_, reject) => setTimeout(() => reject(new Error("isSupported timeout")), 5000))
        ]);
        console.log("isSupported result:", supported);
      } catch (error) {
        console.error("Error checking isSupported:", error);
        // フォールバック: 基本的な環境チェック
        if (typeof window !== "undefined" && "serviceWorker" in navigator) {
          console.log("Using fallback support check");
          supported = true;
        } else {
          console.error("Environment not supported");
          alert("このブラウザではPUSH通知がサポートされていません。");
          return;
        }
      }

      // 2. Service Worker が登録されているか確認
      if (!("serviceWorker" in navigator)) {
        console.error("Service Worker is not supported in this browser.");
        alert("このブラウザではService Workerがサポートされていません。");
        return;
      }

      let registration;
      try {
        registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js", {
          scope: "/"
        });
        console.log("Service Worker 登録成功:", registration);
      } catch (error) {
        console.error("Service Worker 登録失敗:", error);
        alert("Service Workerの登録に失敗しました。");
        return;
      }

      try {
        // 1. ブラウザの標準APIで通知の許可を求める
        const permission = await Notification.requestPermission();
        if (permission !== "granted") {
          console.log("Notification permission not granted.");
          this.showPermissionGuide(permission);
          return;
        }

        console.log("Notification permission granted.");

        // 2. FCM登録トークンを取得
        const token = await getToken(messaging, {
          vapidKey: process.env.VUE_APP_FCM_VAPID_KEY,
          serviceWorkerRegistration: registration
        });

        if (token) {
          console.log("FCM Token:", token);

          // トークンの詳細をデコード（デバッグ用）
          try {
            const tokenParts = token.split(":");
            console.log("Token parts:", tokenParts);
            console.log("Expected project:", process.env.VUE_APP_FCM_PROJECT_ID);
            console.log("Expected sender ID:", process.env.VUE_APP_FCM_MESSAGING_SENDER_ID);
          } catch (e) {
            console.log("Token decode error:", e);
          }

          this.currentToken = token;
          await this.sendTokenToServer(token);
          this.isDeviceRegistered = true;
          // ローカルストレージにトークンを保存
          localStorage.setItem("fcm_token", token);
          alert(`PUSH通知の${this.isDeviceRegistered ? "更新" : "登録"}が完了しました。`);
        } else {
          console.log("No registration token available.");
          alert("登録トークンを取得できませんでした。");
        }
      } catch (err) {
        console.error("Unable to get permission to notify.", err);

        // エラーの詳細を確認
        if (err.code === "messaging/failed-service-worker-registration") {
          console.error("Service Worker registration failed");
          alert("Service Workerの登録に失敗しました。");
        } else if (err.code === "messaging/permission-blocked") {
          console.error("Notification permission blocked");
          alert("通知がブロックされています。ブラウザの設定を確認してください。");
        } else if (err.code === "messaging/vapid-key-required") {
          console.error("VAPID key is required");
          alert("VAPID keyが必要です。");
        } else {
          console.log(err);
          alert("通知の許可取得中にエラーが発生しました。");
        }
      }
    },
    async sendTokenToServer(token) {
      try {
        await this.postSaveDevice({
          deviceToken: token,
          userAgent: navigator.userAgent
        });
      } catch (error) {
        console.error("Error sending token to server:", error);
        throw error;
      }
    },
    showPermissionGuide(permission) {
      this.permissionStatus = permission;
      this.showGuide = true;
    },
    getPermissionMessage() {
      switch (this.permissionStatus) {
        case "denied":
          return "通知がブロックされています。ブラウザの設定で通知を許可してください。";
        case "default":
          return "通知の許可がまだ設定されていません。手動で設定してください。";
        default:
          return "通知の許可が得られませんでした。";
      }
    },
    async retryPermission() {
      this.showGuide = false;
      await this.registerForNotifications();
    },
    checkCurrentPermissionStatus() {
      if ("Notification" in window) {
        this.currentPermissionStatus = Notification.permission;
      }
    },
    getPermissionStatusText() {
      switch (this.currentPermissionStatus) {
        case "granted":
          return "許可済み";
        case "denied":
          return "ブロック中";
        case "default":
          return "未設定";
        default:
          return "不明";
      }
    },
    getPermissionChipColor() {
      switch (this.currentPermissionStatus) {
        case "granted":
          return "success";
        case "denied":
          return "error";
        case "default":
          return "warning";
        default:
          return "grey";
      }
    },
    async checkDeviceRegistration() {
      // 通知許可がない場合は確認不要
      if (this.currentPermissionStatus !== "granted") {
        this.isDeviceRegistered = false;
        return;
      }

      try {
        // 現在のFCMトークンを取得して確認
        if (!messaging) {
          return;
        }

        // Service Worker登録確認
        const registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js", {
          scope: "/"
        });

        const token = await getToken(messaging, {
          vapidKey: process.env.VUE_APP_FCM_VAPID_KEY,
          serviceWorkerRegistration: registration
        });

        if (token) {
          this.currentToken = token;
          // ローカルストレージに保存されたトークンと比較
          const savedToken = localStorage.getItem("fcm_token");
          this.isDeviceRegistered = savedToken === token;
        }
      } catch (error) {
        console.error("Error checking device registration:", error);
        this.isDeviceRegistered = false;
      }
    },
    getOsInfo(device) {
      if (!device.deviceInfo) return "不明";
      const os = device.deviceInfo.operatingSystemName || "不明";
      const version = device.deviceInfo.operatingSystemVersion;
      return version ? `${os} ${version}` : os;
    },
    getBrowserInfo(device) {
      if (!device.deviceInfo) return "不明";
      const browser = device.deviceInfo.agentName || "不明";
      const version = device.deviceInfo.agentVersion;
      return version ? `${browser} ${version}` : browser;
    },
    formatDateTime(dateTime) {
      if (!dateTime) return "-";
      const date = new Date(dateTime);
      return date.toLocaleString("ja-JP", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit"
      });
    },
    confirmDeleteDevice(device) {
      this.deviceToDelete = device;
      this.$refs.deleteConfirmDialog.openDialog();
    },
    async executeDeleteDevice() {
      if (!this.deviceToDelete) return;

      this.deletingDeviceId = this.deviceToDelete.id;
      try {
        await this.deleteDevice(this.deviceToDelete.id);
        // alert("デバイスを削除しました。");
        // デバイスリストを再読み込み
        await this.loadDeviceList();
        // 削除したデバイスが現在のデバイスの場合、登録状態をリセット
        if (this.currentToken === this.deviceToDelete.deviceToken) {
          this.isDeviceRegistered = false;
          localStorage.removeItem("fcm_token");
        }
      } catch (error) {
        console.error("Error deleting device:", error);
        if (error.response && error.response.data && error.response.data.message) {
          alert(`デバイスの削除に失敗しました: ${error.response.data.message}`);
        } else {
          alert("デバイスの削除に失敗しました。");
        }
      } finally {
        this.deletingDeviceId = null;
        this.deviceToDelete = null;
      }
    }
  }
};
</script>

<style scoped lang="stylus">
.v-subheader {
  height: 36px;
}
</style>
